import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { complianceApi, type EInvoiceGstinDetails } from '@/api/compliance.api';
import { useToast } from '@/contexts/ToastContext';

interface GstinLookupModalProps {
  onClose: () => void;
  initialGstin?: string;
}

export default function GstinLookupModal({ onClose, initialGstin = '' }: GstinLookupModalProps) {
  const navigate = useNavigate();
  const toast = useToast();

  const [gstin, setGstin] = useState(initialGstin.toUpperCase());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EInvoiceGstinDetails | null>(null);
  const [error, setError] = useState<{ message: string; needsAuth: boolean } | null>(null);

  const handleLookup = async () => {
    const clean = gstin.trim().toUpperCase();
    if (clean.length !== 15) {
      toast.error('Enter a valid 15-character GSTIN');
      return;
    }

    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await complianceApi.lookupEInvoiceGstin(clean);
      if (res?.success && res.data) {
        setResult(res.data);
      } else {
        setError({
          message: res?.message || 'GSTIN lookup failed',
          needsAuth: Boolean(res?.authRequired) || res?.errorCode === 'AUTH_REQUIRED',
        });
      }
    } catch (err: any) {
      setError({ message: err?.message || 'GSTIN lookup failed', needsAuth: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in-95 duration-150 border border-slate-100">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="w-10 h-10 rounded-full bg-indigo-50 text-[#4f46e5] flex items-center justify-center mb-3">
          <i className="ri-search-eye-line text-xl" />
        </div>

        <h3 className="text-base font-bold text-gray-900 mb-0.5">GSTIN Lookup</h3>
        <p className="text-xs text-gray-500 mb-4">
          Verify a GSTIN's live registration status on the e-Invoice (NIC) system before generating an invoice for it.
        </p>

        <div className="flex items-center gap-2 mb-4">
          <input
            type="text"
            value={gstin}
            onChange={(e) => setGstin(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
            maxLength={15}
            placeholder="e.g. 27AADCR4849M1ZV"
            className="flex-1 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent uppercase"
          />
          <button
            onClick={handleLookup}
            disabled={loading || gstin.trim().length !== 15}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-search-line" />}
            Lookup
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 space-y-2">
            <div className="flex items-start gap-2">
              <i className="ri-error-warning-line text-sm shrink-0 mt-0.5" />
              <span>{error.message}</span>
            </div>
            {error.needsAuth && (
              <button
                onClick={() => navigate('/compliance/e-invoice/settings')}
                className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 underline hover:text-red-800 cursor-pointer"
              >
                Go to E-Invoice Settings to authenticate
              </button>
            )}
          </div>
        )}

        {result && (
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-3.5 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-bold text-gray-900">{result.legalName || '—'}</span>
                <span
                  className={`px-2 py-0.5 text-[10px] font-extrabold uppercase rounded ${
                    result.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {result.status === 'ACT' ? 'Active' : result.status || 'Unknown'}
                </span>
              </div>
              {result.tradeName && result.tradeName !== result.legalName && (
                <p className="text-xs text-gray-500">Trade Name: {result.tradeName}</p>
              )}
              <p className="text-xs font-mono text-gray-600 mt-1">{result.gstin}</p>
            </div>

            <div className="p-3.5 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">Taxpayer Type</span>
                <span className="font-semibold text-gray-800">{result.taxpayerTypeLabel}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">State Code</span>
                <span className="font-semibold text-gray-800">{result.stateCode || '—'}</span>
              </div>
              {result.pincode && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Pincode</span>
                  <span className="font-semibold text-gray-800">{result.pincode}</span>
                </div>
              )}
              {result.address && (
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500 shrink-0">Address</span>
                  <span className="font-semibold text-gray-800 text-right">{result.address}</span>
                </div>
              )}
              {result.registeredOn && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Registered On</span>
                  <span className="font-semibold text-gray-800">{result.registeredOn}</span>
                </div>
              )}
              {result.isBlocked && (
                <div className="flex items-start gap-1.5 p-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 mt-2">
                  <i className="ri-alert-line shrink-0 mt-0.5" />
                  <span>This GSTIN is currently blocked for e-Invoice/e-Way Bill generation due to non-filing of returns.</span>
                </div>
              )}
              {!result.isActive && (
                <div className="flex items-start gap-1.5 p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 mt-2">
                  <i className="ri-close-circle-line shrink-0 mt-0.5" />
                  <span>This GSTIN is not active. IRN generation will be rejected by the e-Invoice system.</span>
                </div>
              )}
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-4 w-full py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
        >
          Close
        </button>
      </div>
    </div>
  );
}
